import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepicker,
  NgbTimeStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { NgSelectComponent } from '@ng-select/ng-select';
import * as moment from 'moment-timezone';
import { DateFormat } from '../../enums/date.enum';

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '/';
  time: NgbTimeStruct;

  readonly ngbNullDate = new NgbDate(new Date(0).getFullYear(), 1, 1);

  DateFormat = DateFormat;

  constructor(protected datePipe: DatePipe) {
    super();
  }

  setTime(time: NgbTimeStruct): void {
    this.time = time;
  }

  customFormat(time: any): any {
    return this.datePipe.transform(time, DateFormat.FullDateTimeCommon);
  }

  parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = new Date(value);
      return {
        day: date.getDate(),
        month: date.getMonth() + 0,
        year: date.getFullYear(),
      };
    }
    return null;
  }

  format(date: NgbDateStruct | any | null): string {
    const hour = this.time ? this.time.hour : 0;
    const minute = this.time ? this.time.minute : 0;
    const second = this.time ? this.time.second : 0;

    if (date && !this.ngbNullDate.equals(date)) {
      const time = new Date(
        date.year,
        date.month - 1,
        date.day,
        hour,
        minute,
        second
      );

      return this.customFormat(time);
    }
    return '';
  }
}

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParser2Formatter extends CustomDateParserFormatter {
  constructor(protected datePipe: DatePipe) {
    super(datePipe);
  }

  customFormat(time): any {
    return this.datePipe.transform(time, 'mediumDate');
  }
}

interface TimeZone {
  name: string;
  tzCode: string;
}

interface TimezoneGroup {
  offset: string;
  timezones: string[];
}

@Component({
  selector: 'app-boostrap-datepicker',
  templateUrl: './boostrap-datepicker.component.html',
})
export class BoostrapDatepickerComponent implements OnInit, OnChanges {
  @Input() placeholder = '';
  @Input() currentTime = '';
  @Input() editable: boolean;
  @Input() isOptional: boolean;

  @Input() hideTimeOfDate = false;
  @Input() pastTime = false;
  @Input() futureTime = false;
  @Input() shouldUseDefaultDate = false;

  @Input() minTime: NgbDateStruct;
  @Input() maxTime: NgbDateStruct;

  @Input() inputId = 'boostrap-datepicker-input-id';
  @Input() placement: PlacementArray = 'left auto';
  @Input() defaultDateTime: string;

  @Input() showTimeZone: boolean = false;
  @Input() Timezone: string;

  @Output() dateTimeChange = new EventEmitter<string>();
  @Output() setTimeZone = new EventEmitter<string>();

  date: NgbDateStruct = null;
  @Input() time: NgbTimeStruct;

  currentValue: {
    date: NgbDateStruct;
    time: NgbTimeStruct;
    timeZone: TimeZone;
  };
  timeIsDifferent = false;

  @ViewChild('datePicker') datePicker: NgbInputDatepicker;
  @ViewChild('selectComp') selectComp: NgSelectComponent;

  timezones: TimeZone[] = [];
  selectedTimezone: TimeZone;
  currentIndex = 0;
  timezoneGroups: TimezoneGroup[] = [];

  constructor(
    private calendar: NgbCalendar,
    private dateParser: NgbDateParserFormatter,
    private datePipe: DatePipe
  ) {
    this.setDefaultTime();
    this.initTimezones();
  }

  ngOnInit(): void {
    if (this.isNullDate(this.date)) {
      this.date = null;
    }
    this.selectedTimezone = this.getLocalTimezone();
    this.emitValue(false);
    this.limitCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pastTime && changes.futureTime) {
      this.limitCalendar();
    }

    if (
      (changes.currentTime &&
        changes.currentTime.previousValue !==
          changes.currentTime.currentValue) ||
      (changes.Timezone && !changes.currentTime)
    ) {
      if (this.currentTime != null) {
        const tz =
          this.Timezone ||
          this.selectedTimezone?.tzCode ||
          this.getLocalTimezone().tzCode;
        const tzMoment = moment.utc(this.currentTime).tz(tz);
        this.date = {
          year: tzMoment.year(),
          month: tzMoment.month() + 1,
          day: tzMoment.date(),
        };
        this.time = {
          hour: tzMoment.hour(),
          minute: tzMoment.minute(),
          second: tzMoment.second(),
        };
      } else {
        this.date = null;
        this.setDefaultTime();
      }
      this.currentValue = {
        date: this.date,
        time: this.time,
        timeZone: this.selectedTimezone,
      };
      // @ts-ignore
      this.dateParser.setTime(this.time);
    }

    if (
      changes.defaultDateTime &&
      changes.defaultDateTime.previousValue !==
        changes.defaultDateTime.currentValue &&
      changes.defaultDateTime.currentValue
    ) {
      const tz =
        this.Timezone ||
        this.selectedTimezone?.tzCode ||
        this.getLocalTimezone().tzCode;
      const tzMoment = moment.utc(this.defaultDateTime).tz(tz);
      this.date = {
        year: tzMoment.year(),
        month: tzMoment.month() + 1,
        day: tzMoment.date(),
      };
      this.time = {
        hour: tzMoment.hour(),
        minute: tzMoment.minute(),
        second: tzMoment.second(),
      };
    }

    if (changes.Timezone) {
      this.groupTimezones();
      this.selectedTimezone = this.getTimezone(this.Timezone?.toString());
      if (this.currentTime) {
        const tz =
          this.Timezone ||
          this.selectedTimezone?.tzCode ||
          this.getLocalTimezone().tzCode;
        const tzMoment = moment.utc(this.currentTime).tz(tz);
        this.date = {
          year: tzMoment.year(),
          month: tzMoment.month() + 1,
          day: tzMoment.date(),
        };
        this.time = {
          hour: tzMoment.hour(),
          minute: tzMoment.minute(),
          second: tzMoment.second(),
        };
      }
    }
  }

  limitCalendar(): void {
    const today = this.calendar.getToday();

    if (this.pastTime) {
      this.minTime = this.minTime || this.calendar.getPrev(today, 'y', 100);
      this.maxTime = this.maxTime || this.calendar.getPrev(today);
      this.updateDefaultDate(this.maxTime);
    } else if (this.futureTime) {
      this.minTime = this.minTime || this.calendar.getNext(today);
      this.maxTime = this.maxTime || this.calendar.getNext(today, 'y', 100);
      this.updateDefaultDate(this.minTime);
    } else {
      this.minTime = this.minTime || this.calendar.getPrev(today, 'y', 100);
      this.maxTime = this.maxTime || this.calendar.getNext(today, 'y', 100);
      this.updateDefaultDate(today);
    }
    this.emitValue(false);
  }

  updateDefaultDate(date: NgbDateStruct): void {
    if (this.shouldUseDefaultDate) {
      this.date = this.date || date;
    }
  }

  confirm(): void {
    this.timeIsDifferent = false;
    this.currentValue = {
      date: this.date,
      time: this.time,
      timeZone: this.selectedTimezone,
    };
    this.emitValue();
    if (this.datePicker) {
      this.datePicker.close();
    }
  }

  cancel(): void {
    this.timeIsDifferent = false;

    if (this.currentValue) {
      this.date = this.currentValue.date;
      this.time = this.currentValue.time;
      this.selectedTimezone = this.currentValue.timeZone;
    }

    this.updateDateTime();

    if (this.datePicker) {
      this.datePicker.close();
    }
  }

  clear(): void {
    this.date = null;
    this.setDefaultTime();

    this.confirm();
  }

  isTimeChanged(): boolean {
    if (!this.currentValue || !this.date) return false;

    return (
      this.currentValue.date?.year !== this.date.year ||
      this.currentValue.date?.month !== this.date.month ||
      this.currentValue.date?.day !== this.date.day ||
      this.currentValue.time?.hour !== this.time.hour ||
      this.currentValue.time?.minute !== this.time.minute ||
      this.currentValue.time?.second !== this.time.second ||
      this.currentValue.timeZone?.tzCode !== this.selectedTimezone?.tzCode
    );
  }

  emitValue(willTriggerFormStatusChanged = true): void {
    if (!this.shouldUseDefaultDate && !willTriggerFormStatusChanged) {
      return;
    }

    let formatDate: string;

    try {
      if (!this.date) {
        formatDate = null;
        this.currentValue = null;
        this.dateTimeChange.emit(formatDate);
        return;
      }

      // 1. Get selected Timezone
      const selectedTz = this.selectedTimezone?.tzCode;

      if (selectedTz) {
        // 2. Set datetime depend on selected Timezone
        const adjustedDate = moment.tz(
          {
            year: this.date.year,
            month: this.date.month - 1,
            date: this.date.day,
            hour: this.time.hour,
            minute: this.time.minute,
            second: this.time.second,
          },
          selectedTz
        );

        // 3. Convert to UTC ISO string to ensure consistent timezone handling
        formatDate = adjustedDate.utc().toISOString();
      } else {
        // If don't have selected timezone -> get time base on UTC
        const dateObj = new Date(
          Date.UTC(
            this.date.year,
            this.date.month - 1,
            this.date.day,
            this.time.hour,
            this.time.minute,
            this.time.second
          )
        );
        formatDate = dateObj.toISOString();
      }

      this.currentValue = {
        date: this.date,
        time: this.time,
        timeZone: this.selectedTimezone,
      };
    } catch (error) {
      console.error('Error in emitValue:', error);
      formatDate = null;
      this.currentValue = null;
    }

    this.setTimeZone.emit(this.selectedTimezone?.tzCode);
    this.dateTimeChange.emit(formatDate);
  }

  updateDateTime(): void {
    if (!this.date) return;

    this.date = { ...this.date };
    // @ts-ignore
    this.dateParser.setTime(this.time);
    this.timeIsDifferent = this.isTimeChanged();
    if (this.hideTimeOfDate) {
      this.confirm();
    }
  }

  getFormatedTime(): string {
    if (!this.date) return '-.-';

    const selectedTz = this.selectedTimezone?.tzCode;
    if (selectedTz) {
      const date = moment.tz(
        {
          year: this.date.year,
          month: this.date.month - 1,
          date: this.date.day,
          hour: this.time.hour,
          minute: this.time.minute,
          second: this.time.second,
        },
        selectedTz
      );
      return this.datePipe.transform(
        date.toDate(),
        DateFormat.FullDateTimeCommon
      );
    }
    return this.dateParser.format(this.date) || '-.-';
  }

  getLocalTimezone(): TimeZone {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      name: localTimeZone,
      tzCode: localTimeZone,
    };
  }

  getTimezone(tzCode?: string): TimeZone {
    if (!tzCode) {
      return this.getLocalTimezone();
    }
    return (
      this.timezones.find((tz) => tz.tzCode === tzCode) ||
      this.getLocalTimezone()
    );
  }

  private isNullDate(date: NgbDateStruct): boolean {
    return date && date.year === 1970 && date.day === 1 && date.month === 1;
  }

  private setDefaultTime(): void {
    this.time = {
      hour: 0,
      minute: 0,
      second: 0,
    };
  }

  onTimezoneChange(tz: string) {
    this.selectedTimezone = {
      name: tz,
      tzCode: tz,
    };
  }

  groupTimezones(): void {
    const allTimezones = moment.tz.names();
    const grouped: { [key: string]: string[] } = {};

    // Iterate through all time zones
    allTimezones.forEach((tz) => {
      // Get the standard offset (winter time) in minutes
      // Using January 1st to get standard time offset
      const offsetMinutes = moment.tz('2024-01-01', tz).utcOffset();

      // Convert to hours and format as GMT+/-HH:mm
      const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
      const offsetMins = Math.abs(offsetMinutes) % 60;
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const offsetString = `(GMT${sign}${offsetHours
        .toString()
        .padStart(2, '0')}:${offsetMins.toString().padStart(2, '0')})`;

      // Group time zones by offset
      if (!grouped[offsetString]) {
        grouped[offsetString] = [];
      }
      grouped[offsetString].push(tz);
    });

    // Convert grouped object to array for easier rendering
    this.timezoneGroups = Object.keys(grouped)
      .sort((a, b) => {
        // Sort by offset (numerical comparison)
        const offsetA = this.getOffsetValue(a);
        const offsetB = this.getOffsetValue(b);
        return offsetA - offsetB;
      })
      .map((offset) => ({
        offset,
        timezones: grouped[offset].sort(), // Sort time zones alphabetically within each group
      }));
  }

  // Helper to extract numerical offset for sorting
  private getOffsetValue(offset: string): number {
    const match = offset.match(/([+-])(\d{2}):(\d{2})/);
    if (!match) return 0;
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours * 60 + minutes);
  }

  private initTimezones(): void {
    const allTimezones = moment.tz.names();
    this.timezones = allTimezones.map((tz) => ({
      name: tz,
      tzCode: tz,
    }));
  }

  getLocalTime(dateTime: string): string {
    if (!dateTime) return '';
    const localDate = moment.utc(dateTime).local().toDate();
    return this.datePipe.transform(localDate, DateFormat.FullDateTimeCommon);
  }
}
